package org.vreo.devicehub.features.userAuthentication.presentation.login

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.onStart
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope

import devicehub.composeapp.generated.resources.Res
import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.Result
import org.vreo.devicehub.features.core.domain.onError
import org.vreo.devicehub.features.core.domain.onSuccess
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.userAuthentication.domain.UserAuthenticationRepository
import org.vreo.devicehub.features.userAuthentication.domain.UserRole


/**
 * User authentication view model.
 *
 * @param repository repository for accessing authentication APIs
 */
class UserLoginViewModel(
    private val repository: UserAuthenticationRepository
) : ViewModel() {
    private val _state = MutableStateFlow(UserLoginState())
    val state = _state
        .onStart {
            _state.update {
                it.copy(version = getApplicationVersion())
            }
        }
        .stateIn(
            scope = viewModelScope,
            started = SharingStarted.WhileSubscribed(5000L),
            initialValue = _state.value
        )

    /**
     * Handles user authentication actions.
     *
     * @param action action to handle
     */
    fun onAction(action: UserLoginActions) {
        when (action) {
            is UserLoginActions.OnClearError -> {
                _state.update {
                    it.copy(
                        error = null
                    )
                }
            }

            is UserLoginActions.OnUpdateUsername -> {
                _state.update {
                    it.copy(userName = action.value)
                }
            }

            is UserLoginActions.OnUpdatePassword -> {
                _state.update {
                    it.copy(password = action.value)
                }
            }

            is UserLoginActions.OnLoginClicked -> {
                signIn(action.handler)
            }

            is UserLoginActions.OnRegisterClicked -> {}

            is UserLoginActions.OnForgotPasswordClicked -> {}
        }
    }

    /**
     * Sign in user.
     *
     * @param callback handler/callback called on successfully login
     */
    private fun signIn(callback: ((String, UserRole) -> Unit)? = null) = viewModelScope.launch {
        _state.update { it.copy(isLoading = true) }

        if (!verifyCredentials()) {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.INVALID_CREDENTIALS.toUiText()
                )
            }
            return@launch
        }

        // Login and get authentication token
        val authenticationToken = runCatching {
            val result = repository.signIn(
                username = _state.value.userName,
                password = _state.value.password
            )
            when (result) {
                is Result.Success -> {
                    result.data
                }

                is Result.Error -> {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = result.error.toUiText()
                        )
                    }
                    return@launch
                }
            }
        }.getOrElse {
            _state.update {
                it.copy(
                    isLoading = false,
                    error = DataError.Remote.UNKNOWN.toUiText()
                )
            }
            return@launch
        }

        // Get user's role
        runCatching {
            repository.getUserDetails(authenticationToken)
                .onSuccess { userData ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = null,
                            isAdmin = userData.role == UserRole.VREO_ADMIN.value
                        )
                    }

                    val role = UserRole.entries.find { it.value == userData.role } ?: UserRole.USER

                    if (callback != null) {
                        callback(authenticationToken, role)
                    }
                }
                .onError {
                    _state.update {
                        it.copy(
                            isLoading = false,
                            error = it.error,
                            isAdmin = false
                        )
                    }
                }
        }
    }


    /**
     * Verify credentials.
     *
     * @return true if credentials are valid, false otherwise
     */
    fun verifyCredentials(): Boolean {
        val emptyUsername = state.value.userName.isEmpty()
        val emptyPassword = state.value.password.isEmpty()

        _state.update {
            it.copy(
                emptyUsername = emptyUsername,
                emptyPassword = emptyPassword
            )
        }

        return !emptyUsername && !emptyPassword
    }

    /**
     * Get application version
     *
     * @return application version
     */
    private suspend fun getApplicationVersion(): String {
        return try {
            val jsonString = Res.readBytes("files/version.json").decodeToString()
            val jsonElement = Json.parseToJsonElement(jsonString)
            jsonElement.jsonObject["version"]?.jsonPrimitive?.content ?: "Unknown"
        } catch (_: Exception) {
            "Unknown"
        }
    }
}